import React from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, LoaderCircleIcon, ShieldAlertIcon } from 'lucide-react';
import type { CrmRecord } from '../../lib/crm';
import { INTEREST_LABELS, STATUS_LABELS, formatDateTime, prospectId, value } from '../../lib/crm';
import type { ProspectDuplicateState } from './useProspectDuplicateCheck';
interface ProspectDuplicateNoticeProps {
  state: ProspectDuplicateState;
  similarConfirmed: boolean;
  onSimilarConfirmed: (value: boolean) => void;
  nonBlocking?: boolean;
}
export function ProspectDuplicateNotice({
  state,
  similarConfirmed,
  onSimilarConfirmed,
  nonBlocking = false
}: ProspectDuplicateNoticeProps) {
  if (state.status === 'idle') return null;
  if (state.status === 'checking') {
    return <p className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-cyan-200" data-id="element-452">
        <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" data-id="element-453" />
        Verificando identificadores…
      </p>;
  }
  if (state.status === 'available') {
    return <p className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-emerald-300" data-id="element-454">
        <CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-455" />
        Datos disponibles
      </p>;
  }
  if (state.status === 'error') {
    return <p role="status" className="mt-3 text-xs font-medium text-amber-200" data-id="element-456">
        {state.message}
      </p>;
  }
  const exact = state.status === 'exact';
  const records = exact ? state.exactMatches : state.similarMatches;
  return <section className={`mt-5 rounded-2xl border p-5 ${exact ? 'border-red-400/35 bg-red-400/10' : 'border-amber-300/35 bg-amber-300/10'}`} aria-live="polite" data-id="element-457">
      <div className="flex gap-3" data-id="element-458">
        {exact ? <ShieldAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-200" data-id="element-459" /> : <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-200" data-id="element-460" />}
        <div data-id="element-461">
          <h2 className={`font-bold ${exact ? 'text-red-100' : 'text-amber-100'}`} data-id="element-462">
            {exact ? 'Este negocio ya existe en TITÁN OS.' : 'Encontramos negocios con nombres parecidos.'}
          </h2>
          <p className="mt-1 text-sm text-slate-200" data-id="element-463">
            {exact ? nonBlocking ? 'Es una alerta para revisar el registro existente; no impide guardar esta nueva oportunidad.' : 'No podés guardar otro registro con los mismos identificadores.' : nonBlocking ? 'Es una alerta para revisar los resultados; no impide guardar.' : 'Revisá los resultados antes de confirmar que se trata de un negocio diferente.'}
          </p>
        </div>
      </div>

      <ul className="mt-4 space-y-3" data-id="element-464">
        {records.map((record, index) => <MatchCard key={prospectId(record) || index} record={record} exact={exact} data-id="element-465" />)}
      </ul>

      {exact ? <div className="mt-5 flex flex-wrap gap-3" data-id="element-466">
          <a href={`/admin/prospectos/${prospectId(records[0])}`} className="inline-flex min-h-10 items-center rounded-xl bg-red-200 px-3 text-sm font-bold text-red-950" data-id="element-467">
            Abrir prospecto existente
          </a>
          <a href={`/admin/prospectos/${prospectId(records[0])}`} className="inline-flex min-h-10 items-center rounded-xl border border-red-200/35 px-3 text-sm font-semibold text-red-100" data-id="element-468">
            Registrar actividad
          </a>
          <a href="/admin/prospectos" className="inline-flex min-h-10 items-center rounded-xl border border-white/15 px-3 text-sm font-semibold text-slate-100" data-id="element-469">
            Volver al listado
          </a>
        </div> : <label className="mt-5 flex items-start gap-3 text-sm text-amber-50" data-id="element-470">
          <input type="checkbox" checked={similarConfirmed} onChange={event => onSimilarConfirmed(event.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-300" data-id="element-471" />
          Revisé las coincidencias y confirmo que es un negocio diferente.
        </label>}
    </section>;
}
function MatchCard({
  record,
  exact
}: {
  record: CrmRecord;
  exact: boolean;
}) {
  const id = prospectId(record);
  const matchingField = value(record, 'matched_field', 'match_field', 'identifier_type', 'field');
  const matchingValue = value(record, 'matched_value', 'match_value', 'identifier_value', 'value');
  const similarity = Number(value(record, 'similarity', 'similarity_score'));
  return <li className="rounded-xl border border-white/10 bg-[#07111f]/70 p-4" data-id="element-472">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-473">
        <div data-id="element-474">
          <p className="font-semibold text-white" data-id="element-475">
            {value(record, 'business_name', 'name') || 'Prospecto existente'}
          </p>
          {exact ? <p className="mt-1 text-xs text-red-100" data-id="element-476">
              Coincidencia: {matchingField || 'identificador'} ·{' '}
              {matchingValue || 'valor registrado'}
            </p> : <p className="mt-1 text-xs text-amber-100" data-id="element-477">
              Similitud:{' '}
              {Number.isFinite(similarity) ? `${Math.round(similarity * 100)}%` : 'posible coincidencia'}
            </p>}
        </div>
        {id && <a href={`/admin/prospectos/${id}`} className="text-xs font-semibold text-cyan-200" data-id="element-478">
            Abrir
          </a>}
      </div>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2" data-id="element-479">
        <Info label="Estado" value={STATUS_LABELS[value(record, 'status')] || value(record, 'status') || '—'} data-id="element-480" />
        <Info label="Interés" value={INTEREST_LABELS[value(record, 'interest_level')] || value(record, 'interest_level') || '—'} data-id="element-481" />
        <Info label="Responsable" value={value(record, 'assigned_name', 'responsible_name', 'assigned_to_name') || '—'} data-id="element-482" />
        <Info label="Creado por" value={value(record, 'created_by_name', 'creator_name', 'created_by_email') || '—'} data-id="element-483" />
        <Info label="Creado" value={formatDateTime(value(record, 'created_at'))} data-id="element-484" />
        <Info label="Próximo seguimiento" value={formatDateTime(value(record, 'next_follow_up_at', 'follow_up_at'))} data-id="element-485" />
      </dl>
    </li>;
}
function Info({
  label,
  value: content
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-486">
      <dt className="text-slate-500" data-id="element-487">{label}</dt>
      <dd className="mt-0.5 break-words text-slate-200" data-id="element-488">{content}</dd>
    </div>;
}